import { useState, useEffect } from "react";
import { useDidUpdate } from "@/hooks/useDidUpdate";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Badge from "@/components/ui/badge/Badge";
import DateRangeFilter, {
  getDefaultDateRange,
} from "@/components/common/DateRangeFilter";
import { activityLogService } from "@/services/activityLogService";
import { handleApiError } from "@/services/api";
import Pagination from "@/components/ui/pagination/Pagination";
import type { ActivityLog } from "@/types/models";

const selectClass =
  "h-8 rounded-lg border border-gray-300 bg-transparent px-2 text-xs text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

const entityTypeLabels: Record<string, string> = {
  user: "User",
  shed: "Shed",
  flock: "Flock",
  raw_material: "Raw Material",
  feed_formula: "Feed Formula",
  supplier: "Supplier",
  buyer: "Buyer",
  feed_log: "Feed Log",
  egg_production: "Egg Production",
  mortality_log: "Mortality",
  stock: "Stock",
  feed_production: "Feed Production",
  egg_sale: "Egg Sale",
  bird_sale: "Bird Sale",
  manure_sale: "Manure Sale",
  bag_sale: "Bag Sale",
  expense_category: "Expense Category",
  expense: "Expense",
  payable: "Payable",
  payment: "Payment",
};

const actionColors: Record<string, "success" | "info" | "warning" | "error"> = {
  // The one entry that means someone overrode a money control.
  credit_override: "warning",
  create: "success",
  update: "info",
  deactivate: "warning",
  reset_pin: "warning",
  bulk_create: "success",
};

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function ActivityLogPage() {
  const [entries, setEntries] = useState<ActivityLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [filterFrom, setFilterFrom] = useState(
    () => getDefaultDateRange().from
  );
  const [filterTo, setFilterTo] = useState(() => getDefaultDateRange().to);
  const [filterEntityType, setFilterEntityType] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 25;

  const loadLogs = async (
    fromDate?: string,
    toDate?: string,
    newOffset?: number
  ) => {
    try {
      setLoading(true);
      setError("");
      const params: Record<string, string | number> = { limit };
      const f = fromDate !== undefined ? fromDate : filterFrom;
      const t = toDate !== undefined ? toDate : filterTo;
      const o = newOffset !== undefined ? newOffset : offset;
      if (f) params.from = f;
      if (t) params.to = t;
      if (filterEntityType) params.entity_type = filterEntityType;
      if (o > 0) params.offset = o;

      const res = await activityLogService.list(params);
      if (Array.isArray(res)) {
        setEntries(res as unknown as ActivityLog[]);
        setTotal((res as unknown as ActivityLog[]).length);
      } else {
        const paginated = res as unknown as {
          data: ActivityLog[];
          meta: { total: number };
        };
        setEntries(paginated.data ?? []);
        setTotal(paginated.meta?.total ?? 0);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateRangeApply = (from: string, to: string) => {
    setFilterFrom(from);
    setFilterTo(to);
    setOffset(0);
    loadLogs(from, to, 0);
  };

  const handleFilter = () => {
    setOffset(0);
    loadLogs(undefined, undefined, 0);
  };

  // A dropdown applies the moment it changes; there is no separate Filter button.
  useDidUpdate(handleFilter, [filterEntityType]);

  const handlePrev = () => {
    const newOffset = Math.max(0, offset - limit);
    setOffset(newOffset);
    loadLogs(undefined, undefined, newOffset);
  };

  const handleNext = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    loadLogs(undefined, undefined, newOffset);
  };

  return (
    <>
      <PageMeta
        title="Activity Log | Poultry Book"
        description="System activity log"
      />
      <PageBreadcrumb pageTitle="Activity Log" />

      <div className="card">
        <div className="flex items-center justify-between px-5 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Activity Log{" "}
            {total > 0 && (
              <span className="text-sm font-normal text-gray-500">
                ({total})
              </span>
            )}
          </h3>
        </div>

        <div className="flex flex-wrap items-center gap-3 px-5 pb-4 sm:px-6">
          <DateRangeFilter onApply={handleDateRangeApply} />
          <select
            value={filterEntityType}
            onChange={(e) => setFilterEntityType(e.target.value)}
            className={`${selectClass} max-w-[180px]`}
          >
            <option value="">All types</option>
            {Object.entries(entityTypeLabels).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="mx-5 mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500 sm:mx-6">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          ) : entries.length === 0 && !error ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              No activity logs found for the selected filters.
            </div>
          ) : entries.length === 0 ? null : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell isHeader className="px-5 py-3 text-left">
                    Time
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left">
                    User
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left">
                    Action
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left">
                    Type
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left">
                    Summary
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((log) => (
                  <TableRow
                    key={log.id}
                    className="border-t border-gray-100 dark:border-gray-800"
                  >
                    <TableCell className="whitespace-nowrap px-5 py-3 text-xs text-gray-500 dark:text-gray-400">
                      {formatDateTime(log.created_at)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap px-5 py-3 text-sm text-gray-800 dark:text-white/90">
                      {log.user_name}
                    </TableCell>
                    <TableCell className="px-5 py-3">
                      <Badge
                        size="sm"
                        color={actionColors[log.action] || "light"}
                      >
                        {log.action.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap px-5 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {entityTypeLabels[log.entity_type] || log.entity_type}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-800 dark:text-white/90">
                      {log.summary}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <Pagination total={total} limit={limit} offset={offset} onPrev={handlePrev} onNext={handleNext} />
      </div>
    </>
  );
}
